import React, { useMemo, useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Chip, SelectItem, SharedSelection, TmdxSelect } from '@ds';
import { AdminFormRow } from '@ui';
import { noop } from 'lodash-es';
import { OpoOrganizationOrderByOption } from '@/api/apis/opo';
import { useInfiniteOpoListQuery } from '@/queries/opo';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';
import { CreateOpoContactFormValues, OpoOrgMembershipForm } from '../types';
import { getCombinedOpoOrgList, getUpdatedMemberships, sortSelectedItems } from '../utils';
import { opoOrgMapper } from '../../utils';

type Props = {
  isReadOnly?: boolean;
  isRequired?: boolean;
  replace: (items: OpoOrgMembershipForm[]) => void;
};

export const OpoOrgMultiSelect = React.memo(({ isReadOnly = false, isRequired = false, replace }: Props) => {
  const { control } = useFormContext<CreateOpoContactFormValues>();

  const memberships = useWatch({ control, name: 'memberships' });

  const {
    searchValue,
    list: infiniteDropdownOpoOrgList,
    onSearchValue,
    itemsScrollerRef,
    isLoading,
    setAutocompleteOpen: setSelectOpen,
  } = useInfiniteDropdown({
    setValue: noop,
    listQuery: useInfiniteOpoListQuery,
    clearOnSearch: false,
    mapper: opoOrgMapper,
    defaultSortOrder: { orderBy: OpoOrganizationOrderByOption.Name, asc: true },
  });

  const list = useMemo(
    () => getCombinedOpoOrgList(memberships, infiniteDropdownOpoOrgList),
    [infiniteDropdownOpoOrgList, memberships],
  );

  const onSelectionChange = useCallback(
    (keys: SharedSelection) => {
      const selectedIDs = Array.from(keys) as string[];
      const currentMemberships = memberships ?? [];
      const selectedItems = getUpdatedMemberships(selectedIDs, currentMemberships, list);

      replace(selectedItems);
    },
    [memberships, list, replace],
  );

  return (
    <AdminFormRow title="OPO Organization(s)" inputWrapperClass="max-w-160" isRequired={isRequired}>
      <Controller
        name="memberships"
        control={control}
        render={({ field: { value, name, onBlur, disabled }, fieldState: { invalid, error } }) => (
          <TmdxSelect
            name={name}
            onBlur={onBlur}
            isDisabled={isReadOnly || disabled}
            items={list}
            variant="bordered"
            isMultiline
            selectionMode="multiple"
            placeholder="Select OPO Organizations"
            searchValue={searchValue}
            onSearchValue={onSearchValue}
            aria-label="OPO Organizations"
            isInvalid={invalid}
            errorMessage={error?.message}
            selectedKeys={(value as OpoOrgMembershipForm[] | undefined)?.map((m) => m.opoOrganizationId) || []}
            onSelectionChange={onSelectionChange}
            scrollRef={itemsScrollerRef}
            isLoading={isLoading}
            onOpenChange={setSelectOpen}
            renderValue={(items) => {
              const selectedItems = sortSelectedItems(items);

              return (
                <div className="flex gap-2 flex-wrap">
                  {selectedItems.map((item) => (
                    <Chip key={item.value}>{item.name}</Chip>
                  ))}
                </div>
              );
            }}
          >
            {(selectItem) => (
              <SelectItem textValue={selectItem.name} key={selectItem.value}>
                <span className="text-small">{selectItem.name}</span>
              </SelectItem>
            )}
          </TmdxSelect>
        )}
      />
    </AdminFormRow>
  );
});

OpoOrgMultiSelect.displayName = 'OpoOrgMultiSelect';
